import React, { createContext, useContext, useEffect, useState } from 'react';
import { web3AuthService, getStoredUser, storeUser } from '../services/web3Auth';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({
    user: getStoredUser(), // 初始化时从localStorage读取
    isLoading: false,
    error: null,
    walletType: null, // 'ethereum' 或 'solana'
  });

  // 处理认证错误
  const handleAuthError = (error) => {
    setState(prev => ({
      ...prev,
      error: error.message || '认证失败',
      isLoading: false,
    }));
  };

  // 连接以太坊钱包
  const connectEthereumWallet = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // 连接钱包
      const { address, walletClient } = await web3AuthService.connectEthereumWallet();
      
      // 签名认证
      const userProfile = await web3AuthService.signInWithEthereum(address, walletClient);
      
      // 更新状态
      setState({
        user: userProfile,
        isLoading: false,
        error: null,
        walletType: 'ethereum',
      });
      
      // 存储用户信息
      storeUser(userProfile);
      
      return { success: true, user: userProfile };
    } catch (error) {
      console.error('以太坊钱包连接失败:', error);
      handleAuthError(error);
      return { success: false, error };
    }
  };

  // 连接Solana钱包
  const connectSolanaWallet = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // 连接钱包
      const { publicKey } = await web3AuthService.connectSolanaWallet();
      
      // 签名认证
      const userProfile = await web3AuthService.signInWithSolana(publicKey);
      
      // 更新状态
      setState({
        user: userProfile,
        isLoading: false,
        error: null,
        walletType: 'solana',
      });
      
      // 存储用户信息
      storeUser(userProfile);
      
      return { success: true, user: userProfile };
    } catch (error) {
      console.error('Solana钱包连接失败:', error);
      handleAuthError(error);
      return { success: false, error };
    }
  };

  // 断开钱包连接
  const disconnectWallet = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await web3AuthService.disconnectWallet();
      
      setState({
        user: null,
        isLoading: false,
        error: null,
        walletType: null,
      });
      
      return { success: true };
    } catch (error) {
      console.error('断开钱包连接失败:', error);
      handleAuthError(error);
      return { success: false, error };
    }
  };

  // 检查钱包可用性
  const checkWalletAvailability = () => {
    return {
      ethereum: web3AuthService.hasEthereumWallet(),
      solana: web3AuthService.hasSolanaWallet(),
    };
  };

  const value = {
    ...state,
    connectEthereumWallet,
    connectSolanaWallet,
    disconnectWallet,
    checkWalletAvailability,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
